import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Subcategory } from "@/lib/category-service";
import SubCategoryCard from "@/polymet/components/sub-category-card";
import { useState, useEffect } from "react";
import { CategoryService, ProductCategory } from "@/lib/category-service";
import { Loader2 } from "lucide-react";

interface SubCategoryListProps {
  categoryId: string;
  categorySlug: string; // Add categorySlug field
  categoryName: string;
  subCategories: Subcategory[];
  title?: string;
  description?: string;
  className?: string;
}

export default function SubCategoryList({
  categoryId,
  categorySlug,
  categoryName,
  subCategories,
  title,
  description,
  className = "",
}: SubCategoryListProps) {
  const [productCategoriesMap, setProductCategoriesMap] = useState<Record<string, ProductCategory[]>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const categoryService = new CategoryService();
  
  // Fetch product categories for each subcategory
  useEffect(() => {
    const fetchProductCategories = async () => {
      if (subCategories.length === 0) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const productCategoriesObj: Record<string, ProductCategory[]> = {};
        
        // Fetch product categories for each subcategory
        const promises = subCategories.map(async (subcategory) => {
          const productCategories = await categoryService.getProductCategoriesBySubcategoryId(subcategory.id);
          productCategoriesObj[subcategory.id] = productCategories;
        });
        
        await Promise.all(promises);
        setProductCategoriesMap(productCategoriesObj);
      } catch (error) {
        console.error('Error fetching product categories:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProductCategories();
  }, [subCategories]);
  
  return (
    <Card className={`shadow-none ${className}`}>
      <CardHeader>
        <CardTitle className="text-2xl font-bold">
          {title || `${categoryName} Machinery`}
        </CardTitle>
        {description && <p className="text-muted-foreground">{description}</p>}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
            <span className="ml-2 text-lg">Loading subcategories...</span>
          </div>
        ) : (
          <div className="space-y-6">
            {subCategories.map((subCategory, index) => (
              <SubCategoryCard
                key={subCategory.id}
                id={subCategory.id}
                categoryId={categoryId}
                categorySlug={categorySlug}
                slug={subCategory.slug}
                name={subCategory.name}
                image={subCategory.image_url || `/placeholder-subcategory-${(index % 4) + 1}.jpg`}
                productCategories={productCategoriesMap[subCategory.id] || []}
              />
            ))}

            {subCategories.length === 0 && (
              <div className="text-center py-12">
                <p className="text-lg text-muted-foreground">
                  No subcategories found
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
